import { useState } from 'react';
/**
 * Bağlantı ayrıntısı (artboard F_Baglanti): status, why it is needed, exactly what Twin can
 * and cannot do, the scopes, who holds the token and for how long, the last successful call,
 * recent actions from the audit log, Bağla and the 2 s revoke. The panel never shows a token:
 * Bağla only opens the backend's connect page in a new tab. Telegram, Claude and Genesis
 * (0.4.1) take their values in the "Bağlantı değerleri" fields instead: the value goes to
 * the server once and only its last four characters ever come back.
 */

import type { ReactNode } from 'react';
import type { Connection, Settings } from '../api/settings.js';
import { usePanel } from '../app/context.js';
import { Link } from '../app/router.js';
import { ACCESS_LABELS, connectState, holderLabel, isLive, policyById, safeHttpsUrl } from '../app/settings.js';
import { ConnectionSecrets, isSlotConnection } from '../components/connection-secrets.js';
import { Icon } from '../components/icons.js';
import { AuditList, ConnectionPill, ConnectLink, RevokeButton, ServiceGlyph } from '../components/settings.js';
import { DetailAppBar } from '../components/shell.js';
import { formatClock, formatPctValue, formatRelativeClock, formatUsd, formatUsdNumber } from '../lib/format.js';
import { SettingsError } from './settings-sections.js';

function lastCall(connection: Connection, now: number) {
  if (connection.lastSuccessAt === null) {
    return (
      <>
        <span className="conn-facts__strong">Yok</span>
        <span className="ink-2"> · henüz bağlanmadı</span>
      </>
    );
  }
  return (
    <>
      <span className="conn-facts__strong">Başarılı</span>
      <span className="ink-2"> · </span>
      <span className="mono">{formatRelativeClock(connection.lastSuccessAt, now)}</span>
    </>
  );
}

const CONNECT_REASONS: Readonly<Record<string, string>> = {
  anahtar_yok: 'keystring girilmemiş',
  anahtar_okunamadi: 'keystring okunamadı',
  durum_bilinmiyor: 'izin akışı bulunamadı',
  durum_kullanilmis: 'bu izin bağlantısı zaten kullanılmış',
  sure_doldu: 'süre doldu (15 dakika)',
  etsy_access_denied: 'Etsy tarafında izin verilmedi',
};

/** The result Etsy's return hop left in the address (?bagla=bekleniyor|hata&neden=...). */
function ConnectResult({ connectionId }: { connectionId: string }) {
  const [params] = useState(() => new URLSearchParams(typeof window === 'undefined' ? '' : window.location.search));
  const result = params.get('bagla');
  if (connectionId !== 'etsy' || result === null) return null;
  const reason = params.get('neden');
  const text =
    result === 'bekleniyor'
      ? 'İzin alındı; bağlantı sunucuda kuruluyor. Birkaç saniye sonra bu sayfayı yenile: durum "mağaza bağlı" olmalı.'
      : `Bağlantı kurulamadı${reason === null ? '' : `: ${CONNECT_REASONS[reason] ?? reason}`}. Tekrar dene; sürerse Etsy uygulamasındaki geri dönüş adresini kontrol et.`;
  return (
    <p
      className={result === 'bekleniyor' ? 'conn-result conn-result--wait' : 'conn-result conn-result--danger'}
      role="status"
    >
      {text}
    </p>
  );
}

function ConnectActions({ connection }: { connection: Connection }) {
  const state = connectState(connection);
  const applyUrl = connection.status === 'applying' ? safeHttpsUrl(connection.applyUrl) : null;
  const docUrl = safeHttpsUrl(connection.setupDocUrl);
  let primary: ReactNode = null;
  if (state.kind === 'connect') {
    primary = (
      <ConnectLink
        connection={connection}
        state={state}
        className={isLive(connection) ? 'conn-act conn-act--secondary' : 'conn-act conn-act--primary'}
      >
        <span className="conn-act__label">
          {state.label}
          <Icon name="external-link" size={14} />
        </span>
        <span className="conn-act__sub">yeni sekmede açılır</span>
      </ConnectLink>
    );
  } else if (state.kind === 'blocked') {
    primary = (
      <button
        type="button"
        className="conn-act conn-act--blocked"
        disabled
        aria-label={`Bağla: şu an kapalı, ${state.reason}`}
      >
        <span className="conn-act__label">
          <Icon name="lock" size={16} stroke={2} />
          Bağla
        </span>
        <span className="conn-act__sub">{state.reason}</span>
      </button>
    );
  } else if (docUrl) {
    primary = (
      <a
        href={docUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="conn-act conn-act--primary"
        aria-label={`${connection.name}: kurulum adımlarını aç, yeni sekmede`}
      >
        <span className="conn-act__label">
          Kurulum adımları
          <Icon name="external-link" size={14} />
        </span>
        <span className="conn-act__sub">{connection.statusNote || 'adım adım'}</span>
      </a>
    );
  }
  return (
    <div className={applyUrl ? 'conn-acts' : 'conn-acts conn-acts--single'}>
      {primary}
      {applyUrl ? (
        <a
          href={applyUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="conn-act conn-act--secondary conn-act--row"
          aria-label={`Başvuruyu aç: ${connection.name} geliştirici sayfası yeni sekmede açılır`}
        >
          Başvuruyu aç
          <Icon name="external-link" size={16} />
        </a>
      ) : null}
    </div>
  );
}

/** The detail body, shared by the phone screen and the desktop side pane. */
export function ConnectionDetail({
  connection,
  settings,
  now,
  pane = false,
}: {
  connection: Connection;
  settings: Settings;
  now: number;
  pane?: boolean;
}) {
  const Name = pane ? 'h2' : 'h1';
  const Sub = pane ? 'h3' : 'h2';
  const id = `conn-${connection.id}`;
  const docUrl = safeHttpsUrl(connection.setupDocUrl);
  const entries = settings.audit.filter((entry) => entry.target === connection.id);
  const live = isLive(connection);
  const budget = connection.usage ? policyById(settings, connection.usage.budgetPolicy) : undefined;
  return (
    <>
      <ConnectResult connectionId={connection.id} />
      <section className="conn-id drafting" aria-labelledby={`${id}-h`}>
        <div className="conn-id__top">
          <ServiceGlyph connection={connection} variant="large" />
          <div className="conn-id__titles">
            <Name id={`${id}-h`} className="conn-id__name">
              {connection.name}
            </Name>
            <div className="conn-id__meta">{connection.meta} · sahibi Sercan</div>
          </div>
        </div>
        <div className="conn-id__status">
          <ConnectionPill status={connection.status} />
          <span className="conn-id__detail" aria-live="polite">
            {connection.statusDetail}
          </span>
        </div>
        {isSlotConnection(connection.id) ? null : <ConnectActions connection={connection} />}
        <p className="conn-id__note">{connection.connectNote}</p>
      </section>

      <div className="conn-body">
        {isSlotConnection(connection.id) ? <ConnectionSecrets connectionId={connection.id} pane={pane} /> : null}
        <section aria-labelledby={`${id}-why`} className="conn-sec conn-sec--why">
          <div className="conn-sec__head">
            <Sub id={`${id}-why`} className="key ink-2">
              Neden gerekli
            </Sub>
            {docUrl ? (
              <a
                href={docUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="conn-sec__link"
                aria-label="Kurulum adımları, yeni sekmede açılır"
              >
                <Icon name="list-checks" size={16} />
                Kurulum adımları
                <Icon name="chevron-right" size={16} />
              </a>
            ) : null}
          </div>
          <p className="conn-sec__why">{connection.why}</p>
        </section>

        {connection.constraints.length > 0 ? (
          <section aria-labelledby={`${id}-limits`} className="conn-sec">
            <Sub id={`${id}-limits`} className="key ink-2 conn-sec__title">
              Bilinen kısıtlar
            </Sub>
            <ul className="conn-list">
              {connection.constraints.map((text) => (
                <li key={text} className="conn-list__item">
                  <Icon name="info" size={12} stroke={2.25} className="ink-2 conn-list__icon" />
                  <span>{text}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {connection.scopes.length > 0 ? (
          <section aria-labelledby={`${id}-can`} className="conn-sec">
            <Sub id={`${id}-can`} className="key ink-2 conn-sec__title">
              {live ? 'Ne yapabilir · yetkiler' : 'Bağlanınca ne yapabilecek · yetkiler'}
            </Sub>
            <ul className="scopes">
              {connection.scopes.map((scope) => (
                <li key={scope.label} className="scopes__item">
                  <Icon name="check" size={12} stroke={3} />
                  <span className="scopes__text">
                    <span className="scopes__label">{scope.label}</span>
                    <span className="ink-2"> · {scope.detail}</span>
                  </span>
                  <span className={`scope-chip scope-chip--${scope.access}`}>{ACCESS_LABELS[scope.access]}</span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        <section aria-labelledby={`${id}-cannot`} className="conn-sec conn-sec--cannot">
          <Sub id={`${id}-cannot`} className="key ink-2 conn-sec__title">
            Yapamaz
          </Sub>
          <ul className="conn-list">
            {connection.cannot.map((limit) => (
              <li key={limit.text} className="conn-list__item">
                <Icon name="ban" size={12} stroke={2.5} className="ink-2 conn-list__icon" />
                <span>
                  {limit.text}
                  {limit.why ? (
                    <>
                      : <span className="ink-2">{limit.why}</span>
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>

        <dl className="conn-facts" aria-label="Token">
          <div className="conn-facts__row">
            <dt className="key ink-2">Token kimde</dt>
            <dd>
              <span className="conn-facts__strong">{holderLabel(connection.tokenHolder)}</span>
              <span className="ink-2">{connection.tokenHolder ? ' · ajanlar görmez' : ' · token tutulmaz'}</span>
            </dd>
          </div>
          <div className="conn-facts__row">
            <dt className="key ink-2">Token süresi</dt>
            <dd>
              {connection.token
                ? `${connection.token.access}${connection.token.refresh ? ` · ${connection.token.refresh}` : ''}`
                : '-'}
            </dd>
          </div>
          <div className="conn-facts__row">
            <dt className="key ink-2">Son başarılı çağrı</dt>
            <dd>{lastCall(connection, now)}</dd>
          </div>
          {connection.usage ? (
            <div className="conn-facts__row">
              <dt className="key ink-2">Bu ay</dt>
              <dd>
                <span className="mono">
                  {formatUsdNumber(connection.usage.monthApiEquivalent)} /{' '}
                  {budget ? formatUsd(budget.value, 'auto') : '-'}
                </span>
                <span className="ink-2">
                  {' '}
                  · 5 sa <span className="mono">{formatPctValue(connection.usage.window5hPct)}</span> · hafta{' '}
                  <span className="mono">{formatPctValue(connection.usage.weekPct)}</span>
                </span>
              </dd>
            </div>
          ) : null}
          {connection.warmup ? (
            <div className="conn-facts__row">
              <dt className="key ink-2">Isınma</dt>
              <dd>
                <span className="mono">
                  {connection.warmup.day}/{connection.warmup.days}
                </span>{' '}
                gün <span className="ink-2">· günlük tavan </span>
                <span className="mono">{connection.warmup.dailyCap}</span>
              </dd>
            </div>
          ) : null}
          {connection.disabledReason ? (
            <div className="conn-facts__row">
              <dt className="key ink-2">Neden kapalı</dt>
              <dd>{connection.disabledReason}</dd>
            </div>
          ) : null}
        </dl>

        <section aria-labelledby={`${id}-log`} className="conn-sec">
          <div className="conn-sec__head">
            <Sub id={`${id}-log`} className="key ink-2">
              Son işlemler · denetim kaydı
            </Sub>
            <Link to="/kayitlar" className="conn-sec__link">
              Kayıtlar
              <Icon name="chevron-right" size={16} />
            </Link>
          </div>
          <AuditList entries={entries} empty="Henüz işlem yok; her çağrı buraya yazılır." />
        </section>

        <section aria-labelledby={`${id}-revoke`} className="conn-sec conn-sec--revoke">
          <Sub id={`${id}-revoke`} className="sr-only">
            Yetkileri iptal et
          </Sub>
          <RevokeButton connection={connection} />
          <p className="conn-sec__note">{connection.revokeNote}</p>
        </section>
      </div>
    </>
  );
}

/** Phone: Diğer › Ayarlar › Bağlantı. No tab bar; the kill switch is one tap back. */
export function Baglanti({ id }: { id: string }) {
  const state = usePanel();
  const settings = state.settings;
  const connection = settings?.connections.find((item) => item.id === id);
  return (
    <>
      <DetailAppBar
        back={{ label: 'Ayarlar', to: '/ayarlar' }}
        title="Bağlantı"
        titleMono={null}
        segments={null}
        metaKey="Son kontrol"
        metaValue={connection?.lastCheckAt ? formatClock(connection.lastCheckAt) : '-'}
      />
      <main className="conn-detail">
        {!settings ? (
          <SettingsError />
        ) : connection ? (
          <ConnectionDetail connection={connection} settings={settings} now={state.now} />
        ) : (
          <div className="not-found">
            <p className="all-done__title">Bu bağlantı yok</p>
            <Link to="/ayarlar" className="btn btn--primary">
              Ayarlar'a dön
            </Link>
          </div>
        )}
      </main>
    </>
  );
}
